import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useGoogleContacts } from "@/hooks/useGoogleContacts";
import { useCreateContact, useContactLimits } from "@/hooks/api/useContacts";
import GoogleContactsModal from "@/components/onboarding/GoogleContactsModal";
import AddContactManuallyModal from "@/components/onboarding/AddContactManuallyModal";
import PersonalizeContactModal from "./PersonalizeContactModal";
import type { GoogleContact } from "@/types/googleContacts";
import type { ContactCreateUpdateRequest, Contact } from "@/types/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface AddContactFlowProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const AddContactFlow = ({ isOpen, onClose, onSuccess }: AddContactFlowProps) => {
  const { toast } = useToast();
  const createContact = useCreateContact();
  const { contacts, isLoading: isLoadingContacts, loadContacts } = useGoogleContacts();
  const { data: contactLimits } = useContactLimits();

  const [contactImportOption, setContactImportOption] = useState<string>("");
  const [showGoogleContactsModal, setShowGoogleContactsModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [createdContact, setCreatedContact] = useState<Contact | null>(null);

  const handleClose = () => {
    setContactImportOption("");
    onClose();
  };

  const handleImportOptionContinue = async () => {
    if (contactImportOption === "google") {
      try {
        console.log("Starting Google contacts import...");
        const loadedContacts = await loadContacts();
        console.log("Loaded contacts:", loadedContacts?.length || 0);

        if (loadedContacts && loadedContacts.length > 0) {
          setShowGoogleContactsModal(true);
        } else {
          console.log("No contacts found");
          toast({
            title: "No contacts found",
            description: "Your Google account doesn't have any contacts to import.",
          });
        }
      } catch (error: any) {
        console.error("Error accessing Google contacts:", error);

        if (error?.error === 'popup_closed_by_user') {
          toast({
            title: "Authentication cancelled",
            description: "Please try again to import your Google contacts.",
          });
        } else if (error?.error === 'access_denied') {
          toast({
            title: "Access denied",
            description: "Please grant permission to access your contacts.",
          });
        } else if (error?.message?.includes('Google Client ID is not configured')) {
          toast({
            title: "Google integration not configured",
            description: "Please use the manual entry option to add contacts.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error loading contacts",
            description: "Failed to load your Google contacts. Please try the manual entry option instead.",
            variant: "destructive",
          });
        }
      }
    } else if (contactImportOption === "manual") {
      setShowManualModal(true);
    }
  };

  const handleImportContacts = async (selectedContacts: GoogleContact[]) => {
    try {
      if (contactLimits && selectedContacts.length > contactLimits.remaining) {
        toast({
          title: "Contact limit exceeded",
          description: `You can only add ${contactLimits.remaining} more contact(s).`,
          variant: "destructive",
        });
        return;
      }

      for (const contact of selectedContacts) {
        const contactData: ContactCreateUpdateRequest = {
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
        };

        await createContact.mutateAsync(contactData);
      }

      toast({
        title: "Contacts imported successfully",
        description: `${selectedContacts.length} contact(s) have been added.`,
      });

      setShowGoogleContactsModal(false);
      setContactImportOption("");
      handleClose();
      onSuccess?.();
    } catch (error) {
      console.error("Error importing contacts:", error);
    }
  };

  const handleManualContactSave = async (contactData: ContactCreateUpdateRequest) => {
    try {
      const newContact = await createContact.mutateAsync(contactData);

      toast({
        title: "Contact added successfully",
        description: `${contactData.name} has been added to your contacts.`,
      });

      setShowManualModal(false);
      setCreatedContact(newContact);
      setShowPersonalizeModal(true);
    } catch (error) {
      console.error("Error adding contact:", error);
    }
  };

  const handlePersonalizeComplete = () => {
    setShowPersonalizeModal(false);
    setCreatedContact(null);
    setContactImportOption("");
    handleClose();
    onSuccess?.();
  };

  const handlePersonalizeSkip = () => {
    handlePersonalizeComplete();
  };

  return (
    <>
      {/* Initial Dialog */}
      <Dialog open={isOpen && !showGoogleContactsModal && !showManualModal && !showPersonalizeModal} onOpenChange={handleClose}>
        <DialogContent className="max-w-[621px] p-8 md:p-[56px]">
          <DialogHeader className="mb-8">
            <DialogTitle className="text-[20px] md:text-[32px] font-bold text-[#6C4F77] mb-2 font-modernist">
              Import your contacts
            </DialogTitle>
            <DialogDescription className="text-[#666666] text-base text-[14px] md:text-[16px] font-normal">
              Import your contacts so Wabisabi can help you remember the people you care about
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            <div>
              <p className="text-[#666666] text-sm mb-4">Contact Synchronization</p>
              <RadioGroup
                value={contactImportOption}
                onValueChange={setContactImportOption}
                className="space-y-3"
              >
                <Label
                  htmlFor="google"
                  className="flex items-center space-x-3 border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <RadioGroupItem value="google" id="google" />
                  <span className="text-[14px] md:text-base font-normal text-gray-700 flex-1">
                    Google Contacts
                  </span>
                </Label>

                <Label
                  htmlFor="manual"
                  className="flex items-center space-x-3 border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
                >
                  <RadioGroupItem value="manual" id="manual" />
                  <span className="text-[14px] md:text-base font-normal text-gray-700 flex-1">
                    Add contacts manually
                  </span>
                </Label>
              </RadioGroup>
            </div>

            <div className="flex gap-3 !mt-[48px]">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1 border-gray-200 text-gray-600 font-normal text-[14px] font-modernist py-[20px]"
              >
                Cancel
              </Button>

              <Button
                onClick={handleImportOptionContinue}
                disabled={!contactImportOption || isLoadingContacts}
                className="flex-1 bg-[#6C4F77] hover:bg-[#5A3A68] disabled:bg-gray-300 text-white font-bold text-[14px] font-modernist py-[20px]"
              >
                {isLoadingContacts ? "Loading..." : "Continue"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Google Contacts Modal */}
      {showGoogleContactsModal && (
        <GoogleContactsModal
          isOpen={showGoogleContactsModal}
          onClose={() => {
            setShowGoogleContactsModal(false);
            setContactImportOption("");
            handleClose();
          }}
          contacts={contacts}
          onImport={handleImportContacts}
          isLoading={isLoadingContacts}
          remainingSlots={contactLimits?.remaining || 0}
        />
      )}

      {/* Add Contact Manually Modal */}
      {showManualModal && (
        <AddContactManuallyModal
          isOpen={showManualModal}
          onClose={() => {
            setShowManualModal(false);
            setContactImportOption("");
            handleClose();
          }}
          onSave={handleManualContactSave}
        />
      )}

      {/* Personalize Contact Modal */}
      {showPersonalizeModal && createdContact && (
        <PersonalizeContactModal
          isOpen={showPersonalizeModal}
          onClose={handlePersonalizeSkip}
          contact={createdContact}
          onSuccess={handlePersonalizeComplete}
        />
      )}
    </>
  );
};

export default AddContactFlow;
